/**
 * The dashboard session: login, the shared idle clock, and logout.
 *
 * One login covers every tab of the browser (app-js/authStorage). The server
 * records it as a session (orrish-backend staff_sessions) that ends when the
 * teammate logs out with a reason, or -- after the NBFC's idle minutes without
 * an action -- automatically, at their last action.
 *
 * The idle clock is the server's: every response says how long is left
 * (`X-Session-Expires-In`), so the browser and the server agree on what an
 * action is. The deadline is kept in localStorage, so every tab counts down to
 * the same moment, and a logout in one tab signs out all of them.
 */
import Vue from "vue";
import axios from "axios";
import store from "@/store";
import router from "./router";
import urls from "./api-urls";
import { getAuth, setAuth, removeAuth, clearAuth } from "./authStorage";

export const SESSION_KEY = "orrish.session";
export const DEADLINE_KEY = "orrish.session.deadline";
const EVENT_KEY = "orrish.session.event";
const LOCK_KEY = "orrish.session.logoutLock";
const MESSAGE_KEY = "orrish.logoutMessage";

export const LOGOUT_REASONS = [
  { value: "day_out", label: "Day out", icon: "mdi-home-export-outline", hint: "Done for the day" },
  { value: "lunch", label: "Lunch", icon: "mdi-silverware-fork-knife", hint: "Back after lunch" },
  { value: "short_break", label: "Short break", icon: "mdi-coffee-outline", hint: "Back in a few minutes" },
];
export const AUTO_LOGOUT = "auto_logout";
export const REASON_LABELS = {
  day_out: "Day out",
  lunch: "Lunch",
  short_break: "Short break",
  auto_logout: "Auto logout",
};

const SESSION_CODES = ["session_expired", "session_ended", "user_not_found"];
const ACTION_THROTTLE_MS = 5000;

// ---- stored session --------------------------------------------------------------

/** `{key, started_at, tracked, timeout_minutes}` or null. */
export function readSession() {
  try {
    const saved = JSON.parse(getAuth(SESSION_KEY) || "null");
    return saved && typeof saved === "object" ? saved : null;
  } catch (e) {
    return null;
  }
}

export function getDeadline() {
  const value = Number(getAuth(DEADLINE_KEY));
  return value > 0 ? value : null;
}

/** Move the shared idle deadline. Writes only real changes, so a burst of
 * responses does not fire a storage event per response in every other tab. */
export function setDeadline(ms) {
  const current = getDeadline();
  if (ms == null) {
    if (current != null) removeAuth(DEADLINE_KEY);
  } else if (current == null || Math.abs(current - ms) > 1000) {
    setAuth(DEADLINE_KEY, Math.round(ms));
  }
  store.commit("session/SET_DEADLINE", ms == null ? null : Math.round(ms));
}

/** Called with the OTP login response. */
export function saveLogin(result) {
  const policy = (result && result.session_policy) || {};
  const session = (result && result.session) || {};
  const saved = {
    key: session.key || null,
    started_at: session.started_at || null,
    tracked: !!policy.tracked,
    timeout_minutes: Number(policy.timeout_minutes) || 10,
  };
  setAuth(SESSION_KEY, JSON.stringify(saved));
  store.dispatch("session/hydrate");
  setDeadline(Date.now() + saved.timeout_minutes * 60000);
  emit("login");
}

// ---- requests ----------------------------------------------------------------------

function authHeaders() {
  const headers = {};
  const token = getAuth("token");
  const nbfc = getAuth("nbfc_Id");
  if (token) headers.authorization = `Bearer ${token}`;
  if (nbfc) headers["X-NBFC-ID"] = nbfc;
  return headers;
}

/** Every response: pick up how long the session has left. */
export function ingestResponse(response) {
  const headers = (response && response.headers) || {};
  const raw = headers["x-session-expires-in"];
  if (raw == null || raw === "") return;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds)) return;
  setDeadline(seconds < 0 ? null : Date.now() + seconds * 1000);
}

const lastPing = {};

/**
 * "The teammate just did this" for actions that make no request of their own:
 * opening a details page or a read-only dialog. The server counts it only for a
 * page or button code the teammate holds, so a tab switch can never be one.
 */
export function noteAction(code) {
  const codes = [].concat(code || []).filter(Boolean);
  const held = codes.find((c) => store.getters["access/can"](c)) || codes[0];
  if (!held || /\.view$/.test(held) || !getAuth("token")) return;
  const session = readSession();
  if (session && !session.tracked) return;
  const now = Date.now();
  if (lastPing[held] && now - lastPing[held] < ACTION_THROTTLE_MS) return;
  lastPing[held] = now;
  axios
    .post(urls.SESSION_ACTION, { code: held }, { headers: authHeaders() })
    .catch(() => {});
}

// ---- logout --------------------------------------------------------------------------

function emit(type, extra = {}) {
  try {
    localStorage.setItem(EVENT_KEY, JSON.stringify({ type, at: Date.now(), nonce: Math.random(), ...extra }));
  } catch (e) {
    /* ignore */
  }
}

/** The message the login page shows once, e.g. why you were signed out. */
export function takeLogoutMessage() {
  try {
    const message = sessionStorage.getItem(MESSAGE_KEY);
    sessionStorage.removeItem(MESSAGE_KEY);
    return message || "";
  } catch (e) {
    return "";
  }
}

/** Sign this tab out without asking the server (it already knows, or another
 * tab told it). `broadcast` signs every other tab out too. */
export function localLogout({ message = "", broadcast = true } = {}) {
  const code = localStorage.getItem("nbfc-code");
  // The store outlives storage here, because this is a router push rather than
  // a reload: reset the modules that hold the signed-in teammate's state.
  store.dispatch("agentFilters/resetFilters");
  store.dispatch("losFilter/resetFilters");
  store.dispatch("notificationFilters/clear");
  store.dispatch("filterLock/unlockFilters");
  store.dispatch("access/clear");
  store.dispatch("session/clear");
  clearAuth();
  try {
    sessionStorage.clear();
    if (message) sessionStorage.setItem(MESSAGE_KEY, message);
  } catch (e) {
    /* ignore */
  }
  if (broadcast) emit("logout", { message });
  if (router.currentRoute.name !== "login") {
    router.push({ name: "login", params: { code } }).catch(() => {});
  }
}

/**
 * Log out, telling the server why. `reason` is one of LOGOUT_REASONS, or
 * AUTO_LOGOUT from the idle timer -- the server then ends the session at the
 * last action, not now.
 */
export async function logout({ reason, message = "" } = {}) {
  const session = readSession();
  if (getAuth("token") && reason && (!session || session.tracked)) {
    try {
      await axios.post(urls.SESSION_LOGOUT, { reason }, { headers: authHeaders(), timeout: 8000 });
    } catch (e) {
      // Signed out locally regardless: the server closes the session itself
      // once it goes idle, at its last action.
    }
  }
  localLogout({ message });
}

/** One tab performs the auto logout; the rest follow its broadcast. */
export function acquireLogoutLock() {
  try {
    const held = Number(localStorage.getItem(LOCK_KEY));
    if (held && Date.now() - held < 10000) return false;
    localStorage.setItem(LOCK_KEY, String(Date.now()));
    return true;
  } catch (e) {
    return true;
  }
}

/**
 * A 401 on a signed-in request means the token no longer works: the session
 * was logged out or expired, or the teammate was removed. Sign out (no server
 * call -- it already refused us) and say why. Returns true when handled.
 */
export function handleUnauthorized(error) {
  const response = error && error.response;
  const sentToken = error && error.config && error.config.headers && error.config.headers.authorization;
  if (!response || response.status !== 401 || !sentToken || !getAuth("token")) return false;
  const data = response.data || {};
  let message = "";
  if (data.code === "session_expired") message = data.detail || "You were signed out after a period without activity.";
  else if (data.code === "session_ended") message = "Your session has ended. Please log in again.";
  else if (!SESSION_CODES.includes(data.code)) message = "Please log in again.";
  localLogout({ message });
  return true;
}

// ---- wiring ------------------------------------------------------------------------------

/** A partner-portal tab keeps its own sign-in (sessionStorage, app-js/partner-portal):
 *  the console's sign-out, sign-in and idle clock must never reach into it. */
function onPartnerPage() {
  const path = (router.currentRoute && router.currentRoute.path) || window.location.pathname || "";
  return path === "/partner" || path.startsWith("/partner/");
}

function onStorage(event) {
  if (onPartnerPage()) return;
  if (event.key === EVENT_KEY && event.newValue) {
    let payload = {};
    try {
      payload = JSON.parse(event.newValue) || {};
    } catch (e) {
      return;
    }
    if (payload.type === "logout" && getAuth("token") == null) {
      localLogout({ message: payload.message || "", broadcast: false });
    } else if (payload.type === "logout") {
      // The token is cleared before the event is written; seeing one here
      // means another tab has already signed in again. Nothing to do.
    } else if (payload.type === "login" && router.currentRoute.name === "login") {
      // Signed in from another tab: this one is signed in too.
      window.location.reload();
    }
  } else if (event.key === DEADLINE_KEY) {
    const value = Number(event.newValue);
    store.commit("session/SET_DEADLINE", value > 0 ? value : null);
  } else if (event.key === SESSION_KEY) {
    store.dispatch("session/hydrate");
  }
}

export default {
  install(VueCtor = Vue) {
    VueCtor.prototype.$noteAction = noteAction;
    window.addEventListener("storage", onStorage);
    store.dispatch("session/hydrate");
    store.commit("session/SET_DEADLINE", getDeadline());
    // Minute labels only: a 15 s tick turns the minute over on time.
    setInterval(() => store.commit("session/TICK"), 15000);
  },
};
